import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Radar,
  RadarChart as RechartsRadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";
import {
  RADAR_AXES,
  RADAR_AXIS_DISPLAY_NAMES,
  type RadarAxis,
  type RadarScores,
} from "@lemonadetraining/contracts";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";

type Variant = "dashboard" | "full" | "mini";

export interface RadarChartProps {
  scores: RadarScores;
  variant?: Variant;
  onAxisClick?: (axis: RadarAxis) => void;
}

const VARIANT_SIZE: Record<Variant, number> = {
  dashboard: 320,
  full: 500,
  mini: 140,
};

// Room for the axis names: at 75% the two-word names ran past the chart's box.
const OUTER_RADIUS: Record<Variant, string> = { dashboard: "60%", full: "66%", mini: "40%" };
const LABEL_SIZE: Record<Variant, number> = { dashboard: 12, full: 13, mini: 8 };

function pickStrongestWeakest(scores: RadarScores): {
  strongest: RadarAxis;
  weakest: RadarAxis;
} {
  let strongest: RadarAxis = RADAR_AXES[0];
  let weakest: RadarAxis = RADAR_AXES[0];
  for (const axis of RADAR_AXES) {
    if (scores[axis] > scores[strongest]) strongest = axis;
    if (scores[axis] < scores[weakest]) weakest = axis;
  }
  return { strongest, weakest };
}

function buildAriaLabel(scores: RadarScores): string {
  const parts = RADAR_AXES.map(
    (axis) => `${RADAR_AXIS_DISPLAY_NAMES[axis]} ${Math.round(scores[axis])}`
  ).join(", ");
  const { strongest, weakest } = pickStrongestWeakest(scores);
  // Overall = equal-weighted mean of the same 5 canonical axes (SCORING-V1.md
  // §3.4). Computed from `scores` — NOT a separately-persisted
  // profile.overallScore — so this label is deterministic and byte-identical
  // to the mobile contract. RADAR_AXES is the same array/order used for
  // `{parts}` above; this is a locked cross-platform `toBe` string.
  const overall = Math.round(
    RADAR_AXES.reduce((sum, axis) => sum + scores[axis], 0) / RADAR_AXES.length
  );
  return `Pentagon radar chart. ${parts}. Strongest: ${RADAR_AXIS_DISPLAY_NAMES[strongest]}. Weakest: ${RADAR_AXIS_DISPLAY_NAMES[weakest]}. Overall score: ${overall}.`;
}

interface AxisTickProps {
  fontSize?: number;
  score?: number;
  payload?: { value?: RadarAxis };
  x?: number;
  y?: number;
  textAnchor?: string;
  onAxisClick?: (axis: RadarAxis) => void;
}

const ClickableAxisTick: React.FC<AxisTickProps> = ({
  fontSize = 12,
  score,
  payload,
  x = 0,
  y = 0,
  textAnchor,
  onAxisClick,
}) => {
  const axis = payload?.value;
  if (!axis) return null;
  const display = RADAR_AXIS_DISPLAY_NAMES[axis];
  const words = display.split(" ");
  const handleClick = () => onAxisClick?.(axis);
  const handleKey = (e: React.KeyboardEvent<SVGTextElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onAxisClick?.(axis);
    }
  };
  const interactive = Boolean(onAxisClick);
  return (
    <text
      x={x}
      y={y}
      textAnchor={textAnchor as React.SVGAttributes<SVGTextElement>["textAnchor"]}
      data-testid={`radar-axis-label-${axis}`}
      role={interactive ? "button" : undefined}
      aria-label={
        interactive
          ? `${display}${score !== undefined ? ` ${Math.round(score)}` : ""}, open details`
          : undefined
      }
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={interactive ? handleKey : undefined}
      style={{
        cursor: interactive ? "pointer" : "default",
        fontSize,
        fill: "currentColor",
        userSelect: "none",
      }}
    >
      {words.length > 1 ? (
        <>
          <tspan x={x} dy="-0.35em">
            {`${words[0]} `}
          </tspan>
          <tspan x={x} dy="1.1em">
            {words.slice(1).join(" ")}
          </tspan>
        </>
      ) : (
        display
      )}
    </text>
  );
};

// Shrinks to the space it is given (a 360 px phone has less than 320 px of card).
function useFittedSize(max: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(max);
  useLayoutEffect(() => {
    const el = ref.current?.parentElement;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const fit = () => {
      const style = window.getComputedStyle(el);
      const width =
        el.clientWidth -
        parseFloat(style.paddingLeft || "0") -
        parseFloat(style.paddingRight || "0");
      if (width > 0) setSize(Math.min(max, Math.floor(width)));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [max]);
  return { ref, size };
}

const RadarChart: React.FC<RadarChartProps> = ({ scores, variant = "dashboard", onAxisClick }) => {
  const reducedMotion = usePrefersReducedMotion();
  const { ref, size } = useFittedSize(VARIANT_SIZE[variant]);

  const data = useMemo(() => RADAR_AXES.map((axis) => ({ axis, score: scores[axis] })), [scores]);

  const ariaLabel = useMemo(() => buildAriaLabel(scores), [scores]);

  // Render-time instrumentation for the dashboard hero target (<300ms).
  // Scoped to the `dashboard` variant only — the `mini` variant renders 50+
  // times on the coach roster and would otherwise dominate the ring buffer
  // and evict the dashboard samples we actually care about. The `full`
  // variant is non-existent on web (this Recharts implementation only ships
  // dashboard/mini sizes, see VARIANT_SIZE above).
  // Dev-only: each effect-body branch early-returns when NODE_ENV is
  // "production"; Terser dead-code-eliminates the body. Effect runs on
  // [variant, scores] only — same scores reference between renders won't
  // record a duplicate sample.
  const renderStart =
    process.env.NODE_ENV !== "production" &&
    variant === "dashboard" &&
    typeof performance !== "undefined" &&
    typeof performance.now === "function"
      ? performance.now()
      : null;
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    if (renderStart === null) return;
    if (typeof window === "undefined") return;

    const elapsedMs = performance.now() - renderStart;
    type PerfMarks = { radarRenderMs?: Array<{ variant: Variant; ms: number; at: number }> };
    const w = window as Window & { __ltPerfMarks?: PerfMarks };
    w.__ltPerfMarks = w.__ltPerfMarks ?? {};
    const samples = (w.__ltPerfMarks.radarRenderMs = w.__ltPerfMarks.radarRenderMs ?? []);
    samples.push({ variant, ms: elapsedMs, at: Date.now() });
    if (samples.length > 50) samples.shift();
    // renderStart intentionally omitted — it's the start timestamp of THIS
    // render and only meaningful in this effect's closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, scores]);

  // A zero profile would stack every dot at the centre; the mini chart is too small for them.
  const showDots = variant !== "mini" && RADAR_AXES.some((axis) => scores[axis] > 0);

  return (
    // A role="img" hides everything inside it, so the chart is a group when its
    // axis names are buttons.
    <div
      ref={ref}
      data-testid="radar-chart"
      data-variant={variant}
      data-reduced-motion={reducedMotion ? "true" : "false"}
      role={onAxisClick ? "group" : "img"}
      aria-label={ariaLabel}
      style={{ width: size, height: size }}
    >
      <RechartsRadarChart
        width={size}
        height={size}
        data={data}
        outerRadius={OUTER_RADIUS[variant]}
      >
        <PolarGrid />
        <PolarAngleAxis
          dataKey="axis"
          tick={(tickProps: AxisTickProps) => (
            <ClickableAxisTick
              {...tickProps}
              fontSize={LABEL_SIZE[variant]}
              score={tickProps.payload?.value ? scores[tickProps.payload.value] : undefined}
              onAxisClick={onAxisClick}
            />
          )}
        />
        {/* Lock the radial scale to the score range 0-100. Without this Recharts
            auto-scales to the data max, making "STR=40 with all other axes lower"
            render at the outer ring instead of 40% out. */}
        <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
        {/* The ring token keeps a one-axis shape (a single spoke) visible on the
            light card, where primary yellow alone is too faint. */}
        <Radar
          dataKey="score"
          stroke="hsl(var(--ring))"
          strokeWidth={2}
          fill="hsl(var(--primary))"
          fillOpacity={0.4}
          dot={showDots && { r: 3, fill: "hsl(var(--ring))", fillOpacity: 1, strokeWidth: 0 }}
          isAnimationActive={!reducedMotion}
        />
      </RechartsRadarChart>

      <table data-testid="radar-chart-data-table" className="sr-only">
        <caption>Radar scores per axis (0-100)</caption>
        <thead>
          <tr>
            <th scope="col">Axis</th>
            <th scope="col">Score</th>
          </tr>
        </thead>
        <tbody>
          {RADAR_AXES.map((axis) => (
            <tr key={axis}>
              <th scope="row">{RADAR_AXIS_DISPLAY_NAMES[axis]}</th>
              <td>{Math.round(scores[axis])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default React.memo(RadarChart);
